import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Import modular translation files
import commonTranslations from './locales/common.json';
import navigationTranslations from './locales/navigation.json';
import authTranslations from './locales/auth.json';
import pricingTranslations from './locales/pricing.json';
import dashboardTranslations from './locales/dashboard.json';
import dashboardMemoryReadHealthEs from './locales/dashboard.memoryReadHealth.es.json';
import memoriesTranslations from './locales/memories.json';
import homepageTranslations from './locales/homepage.json';
import onboardingTranslations from './locales/onboarding.json';
import integrationsTranslations from './locales/integrations.json';
import careersTranslations from './locales/careers.json';
import footerTranslations from './locales/footer.json';
import settingsTranslations from './locales/settings.json';

// Legacy imports removed - now using modular approach

// Define supported languages
export const SUPPORTED_LANGUAGES = ['en', 'es'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Configure i18next
let i18nConfig = i18n;

// Only use backend in production
if (process.env.NODE_ENV === 'production') {
  i18nConfig = i18nConfig.use(Backend);
}

i18nConfig
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    // Language configuration
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    
    // Only use language detection in production with backend
    // In development, we'll default to resources
    lng: process.env.NODE_ENV === 'development' ? 'en' : undefined,
    
    // Debug mode - reduce verbosity for missing keys
    debug: false, // Set to true only when debugging i18n specifically
    
    // Missing key handling
    saveMissing: false,
    missingKeyHandler: process.env.NODE_ENV === 'development' 
      ? (lng, ns, key) => console.warn(`🌐 Missing translation: ${lng}.${ns}.${key}`)
      : false,
    
    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    
    // Namespace configuration - modular approach
    defaultNS: 'common',
    ns: ['common', 'navigation', 'auth', 'pricing', 'dashboard', 'memories', 'homepage', 'onboarding', 'integrations', 'careers', 'footer', 'settings'],
    
    // Resources (fallback for when backend is not available)
    resources: {
      en: {
        common: commonTranslations,
        navigation: navigationTranslations,
        auth: authTranslations,
        pricing: pricingTranslations,
        dashboard: dashboardTranslations,
        memories: memoriesTranslations,
        homepage: homepageTranslations,
        onboarding: onboardingTranslations,
        integrations: integrationsTranslations,
        careers: careersTranslations,
        footer: footerTranslations,
        settings: settingsTranslations,
      },
      es: {
        // TODO: Create Spanish translations for each module
        common: commonTranslations, // Fallback to English for now
        navigation: navigationTranslations,
        auth: authTranslations,
        pricing: pricingTranslations,
        dashboard: {
          ...dashboardTranslations,
          memoryReadHealth: dashboardMemoryReadHealthEs,
        },
        memories: memoriesTranslations,
        homepage: homepageTranslations,
        onboarding: onboardingTranslations,
        integrations: integrationsTranslations,
        careers: careersTranslations, // Fallback to English for now
        footer: footerTranslations,
        settings: settingsTranslations,
      },
    },
    
    // Detection configuration
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
    
    // Backend configuration (only in production)
    ...(process.env.NODE_ENV === 'production' && {
      backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
        addPath: '/locales/add/{{lng}}/{{ns}}',
        // Handle loading errors gracefully
        allowMultiLoading: false,
        crossDomain: false,
      },
    }),
    
    // React specific options
    react: {
      useSuspense: false, // Disable suspense for better error handling
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
    },
  });

export default i18n;
